<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.vehicle.label', 2), to: '/vehicles' },
      { label: $t('ui.entities.vehicle.model', 2), to: '/vehicles/models' }
    ]"
  />

  <div>
    <!-- Filters -->
    <div class="mb-6 flex flex-wrap items-center gap-4">
      <div class="min-w-64">
        <Label for="manufacturer-filter">Manufacturer</Label>
        <Select
          id="manufacturer-filter"
          v-model="selectedManufacturer"
          :options="manufacturerOptions"
          mode="single"
          searchable
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="All Manufacturers"
          clearable
          @update:model-value="onFilterChange"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </div>

      <div class="min-w-64">
        <Label for="body-style-filter">Body Style</Label>
        <Select
          id="body-style-filter"
          v-model="selectedBodyStyle"
          :options="bodyStyleOptions"
          mode="single"
          searchable
          :chips="false"
          value-key="value"
          label-key="label"
          placeholder="All Body Styles"
          clearable
          @update:model-value="onFilterChange"
        >
          <template #option="slotProps">
            {{ slotProps.option.label }}
          </template>
        </Select>
      </div>

      <div class="flex-1" />

      <Button
        v-if="hasActiveFilters"
        :label="$t('ui.actions.clear', 1)"
        severity="secondary"
        type="button"
        outlined
        icon="filter-slash"
        @click="clearFilters"
      />
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >
      <div
        v-for="i in 8"
        :key="i"
        class="border border-border-secondary rounded-xl bg-quaternary overflow-hidden"
      >
        <!-- Content Section -->
        <div class="p-4 flex flex-col gap-2">
          <!-- Title Skeleton -->
          <Skeleton
            height="1.75rem"
            width="85%"
          />
          <!-- Body Style Chip Skeleton -->
          <div class="mt-0.5">
            <Skeleton
              height="1.1rem"
              width="6rem"
            />
          </div>
        </div>
        <!-- Image Section -->
        <div class="relative w-full flex items-center justify-center p-2">
          <Skeleton
            height="150px"
            width="100%"
          />
        </div>
      </div>
    </div>

    <!-- Grid of Vehicle Models -->
    <div
      v-else-if="filteredModels.length > 0"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >
      <VehicleModelCard
        v-for="model in filteredModels"
        :key="model.id"
        :model="model"
      />
    </div>

    <!-- Empty State -->
    <Empty
      v-else
      title="No vehicle models found"
      description="Try adjusting your filters or check back later."
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Select from '@/components/forms/Select.vue'
import Label from '@/components/forms/Label.vue'
import Button from '@/components/button/Button.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import Empty from '@/components/empty/Empty.vue'
import VehicleModelCard from '@/components/unique/vehicles/VehicleModelCard.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// utilities
import { alertToast } from '@/utilities/notification'

/**
 * Vehicle model data structure
 * @typedef {Object} VehicleModel
 * @property {number} id - Model ID
 * @property {string} make - Manufacturer name
 * @property {string} range - Vehicle range
 * @property {string} model - Model name
 * @property {string} classification - Vehicle classification
 * @property {string} bodyStyle - Body style
 * @property {string|null} image - Image URL
 */

// State
const models = ref([])
const selectedManufacturer = ref(null)
const selectedBodyStyle = ref(null)
const manufacturersList = ref([])

// API requests
const modelsApiRequest = useApiRequest()
const manufacturersApiRequest = useApiRequest()
const isLoading = computed(() => modelsApiRequest.isLoading.value || manufacturersApiRequest.isLoading.value)

// Body style options in alphabetical order
const bodyStyleOptions = [
  { value: 'Campervan', label: 'Campervan' },
  { value: 'Coupe', label: 'Coupe' },
  { value: 'Estate', label: 'Estate' },
  { value: 'Hatchback', label: 'Hatchback' },
  { value: 'MPV', label: 'MPV' },
  { value: 'Saloon', label: 'Saloon' },
  { value: 'SUV', label: 'SUV' },
  { value: 'Van', label: 'Van' }
]

/**
 * Computed property for manufacturer options from manufacturers API
 */
const manufacturerOptions = computed(() => {
  if (!manufacturersList.value || manufacturersList.value.length === 0) {
    return []
  }
  return manufacturersList.value.map(manufacturer => ({
    value: manufacturer.name,
    label: manufacturer.name
  }))
})

/**
 * Computed property to check if any filters are active
 */
const hasActiveFilters = computed(() => {
  return selectedManufacturer.value !== null || selectedBodyStyle.value !== null
})

/**
 * Computed property for filtered models based on selected filters
 */
const filteredModels = computed(() => {
  let filtered = models.value

  // Filter by manufacturer
  if (selectedManufacturer.value) {
    filtered = filtered.filter(model => model.make === selectedManufacturer.value)
  }

  // Filter by body style
  if (selectedBodyStyle.value) {
    filtered = filtered.filter(model => model.bodyStyle === selectedBodyStyle.value)
  }

  return filtered
})

/**
 * Fetch manufacturers list for manufacturer dropdown
 */
function fetchManufacturers() {
  try {
    manufacturersApiRequest
      .send({
        endpoint: '/v1/manufacturers'
      })
      .then(response => {
        if (response.success) {
          manufacturersList.value = response.data
        } else {
          alertToast('Failed to fetch manufacturers', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch manufacturers', err.message, 'error')
  }
}

/**
 * Fetch models from API
 */
function fetchModels() {
  try {
    modelsApiRequest
      .send({
        endpoint: '/v1/vehicles/models'
      })
      .then(response => {
        if (response.success) {
          // API returns an array of objects
          models.value = Array.isArray(response.data) ? response.data : []
        } else {
          alertToast('Error', 'Failed to fetch models', 'error')
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
}

/**
 * Handle filter changes
 */
function onFilterChange() {
  // Filters are handled reactively through computed property
  // This function can be used for additional logic if needed
}

/**
 * Clear all filters
 */
function clearFilters() {
  selectedManufacturer.value = null
  selectedBodyStyle.value = null
}

// Fetch data on component mount
onMounted(() => {
  fetchManufacturers()
  fetchModels()
})
</script>

<style scoped></style>
